// src/lib/briefUtils.js
//
// Shared utilities for the advisor dashboard and case brief page.
// Centralizes lead type derivation, key issues chips, and label helpers
// so these don't drift between pages.
//
// Depends on slotConfig for canonical option labels.

import { getSlotLabel } from './slotConfig'
import { getAdvisorSessionStateLabel } from '../components/HomeLink'

// ── Label helper ──────────────────────────────────────────────────────────────

/**
 * Get a human-readable label for any field value via slotConfig.
 * Emojis are preserved — they help at a glance.
 * Example: slotLabel('comunidad_autonoma', 'andalucia') → 'Andalucía'
 */
export function slotLabel(slotKey, value) {
  if (value == null) return null
  return getSlotLabel(slotKey, value)
}

// ── Basis string cleanup ───────────────────────────────────────────────────────

// Raw DB enum values that appear in runRules basis strings.
// Replace them with human labels when displaying to a gestor.
const BASIS_REPLACEMENTS = {
  // Account bands
  '(over_1m)':       '(over $1M)',
  '(over_500k)':     '(over $500k)',
  '(band_200k_1m)':  '($200k–$1M)',
  '(band_50_200k)':  '($50k–$200k)',
  '(band_10_50k)':   '($10k–$50k)',
  '(under_10k)':     '(under $10k)',
  ' over_1m':        ' over $1M',
  ' over_500k':      ' over $500k',
  ' band_200k_1m':   ' $200k–$1M',
  ' band_50_200k':   ' $50k–$200k',
  ' band_10_50k':    ' $10k–$50k',
  ' under_10k':      ' under $10k',
  // Net worth bands
  ' over_3m':         ' over €3M',
  ' band_2m_3m':      ' €2M–€3M',
  ' band_1m_2m':      ' €1M–€2M',
  ' band_700k_1m':    ' €700k–€1M',
  ' band_300_700k':   ' €300k–€700k',
  ' under_300k':      ' under €300k',
  '(over_3m)':        '(over €3M)',
  '(band_700k_1m)':   '(€700k–€1M)',
  '(band_300_700k)':  '(€300k–€700k)',
  // Residency statuses
  '(resident_full_year)':    '(full-year resident)',
  '(resident_partial_year)': '(partial-year resident)',
  '(non_resident)':          '(non-resident)',
  // Investment types
  '(foreign_brokerage)': '(foreign brokerage)',
  '(spanish_broker)':    '(Spanish broker)',
}

export function cleanBasis(basis) {
  if (!basis) return basis
  let result = basis
  for (const [raw, clean] of Object.entries(BASIS_REPLACEMENTS)) {
    result = result.split(raw).join(clean)
  }
  return result
}

// ── Currency formatting ────────────────────────────────────────────────────
// Shared by AdvisorDashboard's Est. IRPF column and CaseBriefPage's
// calculator estimate section — one place so the two never drift.

export function formatEuro(n) {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
}

// ── Lead type derivation ───────────────────────────────────────────────────────
// Single source of truth — used by AdvisorDashboard and CaseBriefPage.
// Precedence order agreed with product:
// Beckham → Wealth screen → Foreign assets → Autónomo → Capital gains
// → Residency transition → Non-resident → General review
//
// NOTE (2026-07-13): beckham_status is now a clean 'active' / 'not_active'
// binary (see slotConfig.js) — the old 'approved'/'eligible'/'applied'/
// 'considering' granularity was removed. This function was still checking
// those retired values, which meant the Beckham branch could never fire.
// Fixed to check the actual current value.

export function getLeadType(tp, yp) {
  const beckhamActive = tp?.beckham_status === 'active'
  const netWorth = yp?.worldwide_net_worth_band
  const foreign  = yp?.foreign_account_band
  const income   = yp?.income_types || []
  const events   = yp?.major_events || []
  const residency= yp?.spain_residency_status
  const yearType = yp?.residency_year_type

  if (beckhamActive)                                                              return 'Beckham review'
  if (netWorth === 'over_3m')                                                      return 'Wealth tax screen'
  if (['over_1m','over_500k','band_200k_1m'].includes(foreign))                   return 'Foreign asset declaration'
  if (income.includes('self_employment') &&
      ['resident_full_year','resident_partial_year'].includes(residency))         return 'Autónomo setup'
  if (events.some(e => ['sold_property','large_investment_sale'].includes(e)))    return 'Capital gains event'
  if (yearType === 'split_year' || residency === 'resident_partial_year')         return 'Residency transition'
  if (residency === 'non_resident')                                                return 'Non-resident filing'
  return 'General review'
}

// ── Key issues / issue clusters ────────────────────────────────────────────────
// Single ordered pass over the profile, most-material-first. getKeyIssues
// caps at 3 for the dashboard's chip display; getAllIssues returns the full
// list for the case brief, where there's room to show everything relevant.
//
// Full taxonomy (in precedence order):
//   Beckham · Wealth screen · Foreign assets / Modelo 720 possible ·
//   Autónomo · Capital gains · Equity compensation · K-1 / S-corp ·
//   Inheritance / gift · Rental income · US rental · Employment ·
//   No prior filing

function computeIssues(tp, yp) {
  const issues       = []
  const income       = yp?.income_types || []
  const events       = yp?.major_events || []
  const foreign      = yp?.foreign_account_band
  const nw           = yp?.worldwide_net_worth_band
  const beckhamActive = tp?.beckham_status === 'active'
  const filed        = yp?.filed_spain_before
  // NOTE (2026-09-28, ticket 019): tax_year_profiles now has a
  // business_partnership_type column (migration 007), gap-filled on the
  // write side by claim-session. This chip goes live for any
  // caller whose own tax_year_profiles select includes the column — see
  // get-advisor-subject/index.ts and get-advisor-pipeline/index.ts.
  const businessType = yp?.business_partnership_type
  const propertyOwn  = yp?.property_ownership || tp?.property_ownership || []

  if (beckhamActive)                                                              issues.push('Beckham')
  if (nw === 'over_3m')                                                            issues.push('Wealth screen')

  // Modelo 720 is a Beckham exemption — don't flag foreign-asset exposure
  // for someone it doesn't apply to.
  if (!beckhamActive) {
    if (['over_1m','over_500k','band_200k_1m'].includes(foreign))                  issues.push('Foreign assets')
    else if (['band_50_200k','band_10_50k'].includes(foreign))                     issues.push('Modelo 720 possible')
  }

  if (income.includes('self_employment'))                                          issues.push('Autónomo')
  if (events.some(e => ['sold_property','large_investment_sale','sold_crypto'].includes(e))) issues.push('Capital gains')
  if (events.includes('rsu_or_options'))                                            issues.push('Equity compensation')
  if (income.includes('business_partnership') && businessType === 'us_pass_through') issues.push('K-1 / S-corp')
  if (events.includes('inheritance_or_gift'))                                        issues.push('Inheritance / gift')
  if (income.includes('rental') && propertyOwn.includes('us_property'))              issues.push('US rental')
  else if (income.includes('rental'))                                                issues.push('Rental income')
  if (income.some(t => ['employment_es','employment_us'].includes(t)))               issues.push('Employment')
  if (filed === false)                                                                issues.push('No prior filing')

  return issues
}

/** Capped list for dashboard chips (up to 3, most material first). */
export function getKeyIssues(tp, yp) {
  return computeIssues(tp, yp).slice(0, 3)
}

/** Full list for the case brief, where there's room to show everything. */
export function getAllIssues(tp, yp) {
  return computeIssues(tp, yp)
}

// ── Highlights (advisor pipeline table + subject overview) ─────────────────────
// Deliberately dumb: reuses getKeyIssues (already capped/ordered) and
// formatEuro, adds nothing new. Per the Aug 2026 design discussion, this
// stays a simple, product-owned composer — not a cross-product scoring
// engine. Only ever called for a full consented lead row (tp/yp/calc are
// null for a referral_placeholder, which correctly produces an empty list).
export function getHighlights(tp, yp, calc) {
  const highlights = []
  if (calc?.irpf_gross != null) {
    const draftSuffix = calc.is_draft ? ` (${getAdvisorSessionStateLabel('draft').toLowerCase()})` : ''
    highlights.push(`${formatEuro(calc.irpf_gross)} est. IRPF${draftSuffix}`)
  }
  highlights.push(...getKeyIssues(tp, yp))
  return highlights.slice(0, 3)
}